import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { filterNullable, uniq } from "@-/com.npmjs.array-utils-ts";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import {
  BrowseWordsPageContext,
  type HskSortMode,
} from "@-/dev.improve.haniverse.routes.nav.app.browse.tabs.words._context";
import { eq, sql } from "@-/com.npmjs.drizzle-orm";
import runes from "@-/com.npmjs.runes2";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useEffect, useMemo } from "react";
import {
  getHskLevelsForVersion,
  getHskWordsForLevelFromDb,
  isHskLevelKey,
  resolveAvailableHskLevel,
  type HskLevelKey,
} from "@-/dev.improve.haniverse.lib.morpheme.grade.hsk";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { wordCorpusSqlCondition } from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";

interface HskWordRow {
  /** Whether the word passes the user's word corpus (always true without a cutoff). */
  inCorpus: boolean;
  rank: number | null;
  strokeSum: number | null;
  word: string;
}

export type HskWordGridItem = { key: string; simplified: string };

/**
 * The selected level's words, decided once from the query result: still loading (nothing to show
 * yet), failed with nothing to show, or ready — where an empty `gridItems` really means the level
 * has no words. While a different level loads, the previous level's rows stay `ready`
 * (`keepPreviousData`).
 */
export type WordsHskLevelWords =
  | { status: "pending" }
  | { error: Error; retry: () => void; status: "error" }
  | { gridItems: readonly HskWordGridItem[]; status: "ready" };

function compareRows(a: HskWordRow, b: HskWordRow, sortMode: HskSortMode): number {
  if (sortMode === "frequency") {
    const aRank = a.rank ?? Number.POSITIVE_INFINITY;
    const bRank = b.rank ?? Number.POSITIVE_INFINITY;
    if (aRank !== bRank) {
      return aRank - bRank;
    }
    return a.word.localeCompare(b.word);
  }
  if (sortMode === "strokes") {
    const aStrokes = a.strokeSum ?? Number.POSITIVE_INFINITY;
    const bStrokes = b.strokeSum ?? Number.POSITIVE_INFINITY;
    if (aStrokes !== bStrokes) {
      return aStrokes - bStrokes;
    }
    return a.word.localeCompare(b.word);
  }
  return 0;
}

/**
 * The HSK words view model: the selected level (from `BrowseWordsPageContext`), that level's words
 * (a progressive `useQuery` that keeps the previous level on screen while the next one loads) and
 * the sorted grid items. Called directly by `WordsHskViewContent`, its only consumer.
 */
export function useWordsHskView() {
  const [hskLevel, setHskLevel, sortMode, showSingleCharacterWords] = useContextSelectors(
    BrowseWordsPageContext,
    (c) => c.hskLevel,
    (c) => c.setHskLevel,
    (c) => c.hskSortMode,
    (c) => c.showSingleCharacterWords,
  );
  const { hskVersion } = useActorUser();
  const availableLevels = useMemo(() => getHskLevelsForVersion(hskVersion), [hskVersion]);

  // If the selected level isn't valid for the current hsk version (e.g. user picked 79 then
  // switched to hsk2), snap back to level 1.
  useEffect(() => {
    if (
      !isHskLevelKey(hskLevel) ||
      resolveAvailableHskLevel(hskLevel, availableLevels) === undefined
    ) {
      setHskLevel(1);
    }
  }, [availableLevels, hskLevel, setHskLevel]);

  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  // Only the word half of the corpus applies here, so key on it rather than the whole filter.
  const { hanziScript, wordMaxRank } = useMorphemeCorpusFilter();
  // `useQuery` + keepPreviousData keeps the previous level's rows on screen while the next level
  // loads, avoiding a re-suspend of the whole view on every level switch.
  const levelRowsQuery = useQuery({
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<readonly HskWordRow[]> => {
      const learningOrderWords = isHskLevelKey(hskLevel)
        ? await getHskWordsForLevelFromDb(staticDb(import.meta.location), hskVersion, hskLevel)
        : [];
      if (hasLength(learningOrderWords, 0)) {
        return [];
      }

      const db = staticDb(import.meta.location);
      // Evaluated per row in SQL so both scripts' corpora (SUBTLEX `min_rank` / `word_biau`) share
      // one path; without a cutoff every word is in the corpus.
      const corpusCondition = wordCorpusSqlCondition({ hanziScript, wordMaxRank });
      const uniqueGlyphs = uniq(learningOrderWords.flatMap((w) => runes(w)));
      // Rank and stroke totals are independent once the ordered word list is known. JSON set
      // parameters avoid splitting large HSK bands into dozens of D1 HTTP requests.
      const [wordRows, graphemeRows] = await Promise.all([
        db
          .select({
            id: staticSchema.word.id,
            inCorpus:
              corpusCondition === undefined ? sql<number>`1` : sql<number>`${corpusCondition}`,
            rank: staticSchema.wordSubtlex.rank,
            word: staticSchema.word.word,
          })
          .from(staticSchema.word)
          .leftJoin(
            staticSchema.wordSubtlex,
            eq(staticSchema.wordSubtlex.wordId, staticSchema.word.id),
          )
          .where(
            sql`${staticSchema.word.word} IN (SELECT value FROM json_each(${JSON.stringify(learningOrderWords)}))`,
          ),
        hasLength(uniqueGlyphs, 0)
          ? Promise.resolve([])
          : db
              .select({
                glyph: staticSchema.grapheme.glyph,
                strokeCount: staticSchema.grapheme.strokeCount,
              })
              .from(staticSchema.grapheme)
              .where(
                sql`${staticSchema.grapheme.glyph} IN (SELECT value FROM json_each(${JSON.stringify(uniqueGlyphs)}))`,
              ),
      ]);
      const strokeCountByGlyph = new Map<string, number>();
      for (const row of graphemeRows) {
        if (row.strokeCount !== null) {
          strokeCountByGlyph.set(row.glyph, row.strokeCount);
        }
      }

      // Collapse to one row per word form (pick the entry with the best — lowest — rank,
      // falling back to the smallest wordId).
      const byWord = new Map<string, HskWordRow & { wordId: number }>();
      for (const row of wordRows) {
        const characters = runes(row.word);
        let strokeSum: number | null = 0;
        for (const ch of characters) {
          const count = strokeCountByGlyph.get(ch);
          if (count === undefined) {
            strokeSum = null;
            break;
          }
          strokeSum += count;
        }
        const candidate: HskWordRow & { wordId: number } = {
          inCorpus: Boolean(row.inCorpus),
          rank: row.rank,
          strokeSum,
          word: row.word,
          wordId: row.id,
        };
        const existing = byWord.get(row.word);
        if (existing === undefined) {
          byWord.set(row.word, candidate);
          continue;
        }
        const existingRank = existing.rank ?? Number.POSITIVE_INFINITY;
        const candidateRank = candidate.rank ?? Number.POSITIVE_INFINITY;
        if (
          candidateRank < existingRank ||
          (candidateRank === existingRank && candidate.wordId < existing.wordId)
        ) {
          byWord.set(row.word, candidate);
        }
      }

      const rows = filterNullable(
        learningOrderWords.map((wordForm): HskWordRow | undefined => {
          const row = byWord.get(wordForm);
          if (row === undefined) {
            // Not a dictionary word, so it has no corpus rank: out of any cutoff.
            return {
              inCorpus: wordMaxRank === null,
              rank: null,
              strokeSum: null,
              word: wordForm,
            };
          }
          return {
            inCorpus: row.inCorpus,
            rank: row.rank,
            strokeSum: row.strokeSum,
            word: row.word,
          };
        }),
      );
      // HSK bands are a browsing list, not the curriculum: hide words outside the user's corpus.
      return wordMaxRank === null ? rows : rows.filter((row) => row.inCorpus);
    },
    queryKey: ["WordsHskView", hskVersion, hskLevel, hanziScript, wordMaxRank],
  });

  const levelRows = levelRowsQuery.data;
  const gridItems = useMemo(() => {
    if (levelRows === undefined) {
      return undefined;
    }
    const visibleRows = showSingleCharacterWords
      ? levelRows
      : levelRows.filter((row) => runes(row.word).length >= 2);
    const sortedRows =
      sortMode === "learning"
        ? visibleRows
        : [...visibleRows].sort((a, b) => compareRows(a, b, sortMode));
    return sortedRows.map((row, i): HskWordGridItem => ({
      key: `${i}-${row.word}`,
      simplified: row.word,
    }));
  }, [levelRows, showSingleCharacterWords, sortMode]);

  const { error, refetch } = levelRowsQuery;
  const levelWords: WordsHskLevelWords =
    gridItems !== undefined
      ? { gridItems, status: "ready" }
      : error === null
        ? { status: "pending" }
        : {
            error,
            retry() {
              void refetch();
            },
            status: "error",
          };

  return {
    availableLevels,
    hskLevel,
    levelWords,
    onHskLevelChange(level: HskLevelKey) {
      const resolved = resolveAvailableHskLevel(level, availableLevels);
      if (resolved !== undefined) {
        setHskLevel(resolved);
      }
    },
  };
}
